import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';

declare global {
  interface Window {
    google?: {
      accounts: {
        oauth2: {
          initCodeClient: (config: {
            client_id: string;
            scope: string;
            ux_mode?: 'popup' | 'redirect';
            callback: (response: {
              code?: string;
              error?: string;
              error_description?: string;
            }) => void;
          }) => { requestCode: () => void };
        };
      };
    };
  }
}

@Injectable({ providedIn: 'root' })
export class GoogleLinkService {
  private scriptLoaded?: Promise<void>;

  get isConfigured(): boolean {
    return !!environment.googleOAuthClientId?.trim();
  }

  loadScript(): Promise<void> {
    if (typeof document === 'undefined') {
      return Promise.reject(new Error('Google link is browser-only.'));
    }
    if (this.scriptLoaded) {
      return this.scriptLoaded;
    }
    this.scriptLoaded = new Promise((resolve, reject) => {
      if (window.google?.accounts?.oauth2) {
        resolve();
        return;
      }
      const existing = document.getElementById('google-gsi-client');
      if (existing) {
        existing.addEventListener('load', () => resolve());
        existing.addEventListener('error', () =>
          reject(new Error('Failed to load Google sign-in.'))
        );
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.id = 'google-gsi-client';
      script.onload = () => resolve();
      script.onerror = () =>
        reject(new Error('Failed to load Google sign-in.'));
      document.head.appendChild(script);
    });
    return this.scriptLoaded;
  }

  /** GIS popup — backend exchanges code with redirect_uri postmessage. */
  requestAuthCode(): Promise<string> {
    const clientId = environment.googleOAuthClientId?.trim();
    if (!clientId) {
      return Promise.reject(
        new Error('googleOAuthClientId is not configured in environment.')
      );
    }
    return this.loadScript().then(
      () =>
        new Promise<string>((resolve, reject) => {
          const oauth2 = window.google?.accounts?.oauth2;
          if (!oauth2) {
            reject(new Error('Google Identity Services unavailable.'));
            return;
          }
          const client = oauth2.initCodeClient({
            client_id: clientId,
            scope: 'openid email profile',
            ux_mode: 'popup',
            callback: (response) => {
              if (response.error) {
                reject(
                  new Error(
                    response.error_description ||
                      response.error ||
                      'Google authorization failed.'
                  )
                );
                return;
              }
              if (response.code) {
                resolve(response.code);
                return;
              }
              reject(new Error('Google did not return an authorization code.'));
            },
          });
          client.requestCode();
        })
    );
  }
}
